import React, { useState, useEffect, useCallback } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const TYPE_LABELS = {
  contact: "Contact",
  setup: "Setup",
  call: "Call",
  partner: "Partner",
  newsletter: "Newsletter",
};

export default function AdminLeads() {
  const [token, setToken] = useState(() => localStorage.getItem("hubinall_admin_token") || "");
  const [authed, setAuthed] = useState(false);
  const [leads, setLeads] = useState([]);
  const [summary, setSummary] = useState({ total: 0, by_type: {} });
  const [filter, setFilter] = useState("");
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);

  const load = useCallback(async (t) => {
    setLoading(true);
    try {
      const headers = { "x-admin-token": t };
      const [r, s] = await Promise.all([
        api.get("/admin/leads", { headers, params: filter ? { lead_type: filter } : {} }),
        api.get("/admin/leads/summary", { headers }),
      ]);
      setLeads(r.data);
      setSummary(s.data);
      setAuthed(true);
    } catch (err) {
      if (err?.response?.status === 401) { toast.error("Invalid admin token"); setAuthed(false); }
      else toast.error("Failed to load leads");
    } finally { setLoading(false); }
  }, [filter]);

  useEffect(() => { if (token) load(token); /* eslint-disable-next-line */ }, [filter]);

  const submitLogin = (e) => { e.preventDefault(); localStorage.setItem("hubinall_admin_token", token); load(token); };
  const logout = () => { localStorage.removeItem("hubinall_admin_token"); setToken(""); setAuthed(false); };

  if (!authed) {
    return (
      <div className="max-w-md mx-auto px-6 py-24" data-testid="admin-login">
        <div className="mono text-[10px] tracking-[0.3em] uppercase grad-text font-bold mb-5">Admin · Leads</div>
        <h1 className="display text-3xl font-bold mb-8">Enter admin token</h1>
        <form onSubmit={submitLogin} className="space-y-4">
          <input type="password" value={token} onChange={(e) => setToken(e.target.value)} placeholder="ADMIN_TOKEN"
            className="w-full bg-white rounded-xl border hairline py-3 px-4 text-base" data-testid="admin-token-input"/>
          <button type="submit" className="grad-bg text-white rounded-full px-6 py-3 mono text-xs uppercase tracking-[0.2em] font-semibold" data-testid="admin-login-submit">Sign In →</button>
        </form>
        <p className="mt-6 mono text-[10px] tracking-wide text-[#0A0E1A]/55">
          Default token: <code className="grad-text">hubinall-admin-2026</code> (override via backend .env ADMIN_TOKEN).
        </p>
      </div>
    );
  }

  return (
    <div data-testid="admin-leads-page" className="border-b hairline">
      <div className="max-w-[1600px] mx-auto px-6 md:px-10 lg:px-14 py-12">
        <div className="flex flex-wrap items-end justify-between gap-6 mb-10">
          <div>
            <div className="mono text-[10px] tracking-[0.3em] uppercase grad-text font-bold mb-3">Admin · Lead Inbox</div>
            <h1 className="display text-4xl md:text-5xl font-bold tracking-tight">{summary.total} total <span className="grad-text">leads</span></h1>
          </div>
          <div className="flex flex-wrap gap-2 items-center">
            <div className="flex gap-1 panel rounded-full p-1 border hairline">
              <button onClick={() => setFilter("")}
                className={"px-4 py-2 rounded-full mono text-[10px] tracking-[0.2em] uppercase " + (filter === "" ? "grad-bg text-white font-semibold" : "text-[#0A0E1A]/70")}>All</button>
              {Object.keys(TYPE_LABELS).map((k) => (
                <button key={k} onClick={() => setFilter(k)}
                  className={"px-4 py-2 rounded-full mono text-[10px] tracking-[0.2em] uppercase " + (filter === k ? "grad-bg text-white font-semibold" : "text-[#0A0E1A]/70")}
                  data-testid={`filter-${k}`}>
                  {TYPE_LABELS[k]} <span className="grad-text ml-1 font-bold">{summary.by_type?.[k] || 0}</span>
                </button>
              ))}
            </div>
            <button onClick={logout} className="mono text-[10px] tracking-[0.2em] uppercase border hairline px-4 py-2 rounded-full hover:bg-[#F5F6FA]" data-testid="admin-logout">Sign Out</button>
          </div>
        </div>

        {loading && <div className="mono text-sm text-[#0A0E1A]/55">Loading…</div>}

        <div className="bg-white border hairline rounded-[20px] overflow-hidden">
          <div className="hidden md:grid grid-cols-12 gap-4 px-6 py-4 mono text-[10px] tracking-[0.2em] uppercase text-[#0A0E1A]/55 border-b hairline panel">
            <div className="col-span-2">Type</div><div className="col-span-3">Name</div><div className="col-span-3">Email</div>
            <div className="col-span-2">Country</div><div className="col-span-2">Received</div>
          </div>
          {leads.map((l) => (
            <button key={l.id} onClick={() => setSelected(l)}
              className="w-full text-left grid grid-cols-1 md:grid-cols-12 gap-4 px-6 py-4 border-b hairline hover:bg-[#F5F6FA] transition-colors"
              data-testid={`lead-row-${l.id}`}>
              <div className="md:col-span-2 mono text-[11px] tracking-[0.15em] uppercase grad-text font-bold">{TYPE_LABELS[l.type] || l.type}</div>
              <div className="md:col-span-3 display text-base font-semibold">{l.full_name || "—"}</div>
              <div className="md:col-span-3 text-sm text-[#0A0E1A]/70 truncate">{l.email}</div>
              <div className="md:col-span-2 text-sm">{l.country || "—"}</div>
              <div className="md:col-span-2 mono text-xs text-[#0A0E1A]/60">{new Date(l.created_at).toLocaleString()}</div>
            </button>
          ))}
          {!loading && leads.length === 0 && (
            <div className="py-16 text-center text-[#0A0E1A]/55 mono text-sm">No leads yet.</div>
          )}
        </div>
      </div>

      {selected && (
        <div className="fixed inset-0 bg-[#0A0E1A]/70 z-50 flex items-center justify-center p-4" onClick={() => setSelected(null)} data-testid="lead-detail-modal">
          <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-[20px] border hairline max-w-2xl w-full max-h-[80vh] overflow-auto">
            <div className="flex items-center justify-between border-b hairline px-6 py-4">
              <div className="mono text-[11px] tracking-[0.2em] uppercase grad-text font-bold">Lead · {TYPE_LABELS[selected.type]}</div>
              <button onClick={() => setSelected(null)} className="text-[#0A0E1A] hover:text-[#4F46E5]">✕</button>
            </div>
            <div className="p-6">
              <div className="display text-2xl font-bold mb-1">{selected.full_name}</div>
              <div className="mono text-xs text-[#0A0E1A]/55 mb-6">{new Date(selected.created_at).toLocaleString()}</div>
              <pre className="panel rounded-xl p-4 text-xs whitespace-pre-wrap mono leading-relaxed">{JSON.stringify(selected.payload, null, 2)}</pre>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
